import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { WarehouseService } from 'app/modules/components/configuration/warehouse/warehouse.service';
import { MaterialService } from '../material.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { HasPermissionPipe, PermissionDirective, PermissionService } from "app/core/permission";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { MATERIAL_WAREHOUSE_PERMISSIONS } from "app/common/constants/permission";

export interface IWarehouseManagementData {
  materialId: string;
  materialName: string;
  materialCode: string;
  currentWarehouses: string[];
  availableInAllWarehouses: boolean;
  plantId?: string;
}

@Component({
  selector: 'app-material-warehouse-management',
  standalone: true,
  templateUrl: './material-warehouse-management.component.html',
  styleUrls: ['./material-warehouse-management.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    ToggleOptionComponent,
    PermissionDirective,
    HasPermissionPipe,
  ],
})
export class MaterialWarehouseManagementComponent implements OnInit, OnDestroy {
  readonly PERMISSIONS_MATERIAL_WAREHOUSE = MATERIAL_WAREHOUSE_PERMISSIONS;

  // State
  warehouseList: WarehouseAttributes[] = [];
  filteredWarehouses: WarehouseAttributes[] = [];
  selectedWarehouseIds: Set<string> = new Set();
  availableInAll: boolean = false;
  isLoading: boolean = true;
  isSaving: boolean = false;

  // Form Controls
  searchControl = new FormControl<string>('');

  // Original state for change detection
  private originalWarehouseIds: Set<string> = new Set();
  private originalAvailableInAll: boolean = false;

  // Destroy subject
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<MaterialWarehouseManagementComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IWarehouseManagementData,
    private warehouseService: WarehouseService,
    private materialService: MaterialService,
    private snackBar: MatSnackBar,
    private fuseConfirmationService: FuseConfirmationService,
    private readonly permissionService: PermissionService,
    private readonly handlerMessage: HandlerMessageService
  ) {
    // Inicializar estado original
    this.originalWarehouseIds = new Set(data.currentWarehouses || []);
    this.originalAvailableInAll = data.availableInAllWarehouses || false;

    // Inicializar estado actual
    this.selectedWarehouseIds = new Set(data.currentWarehouses || []);
    this.availableInAll = data.availableInAllWarehouses || false;
  }

  ngOnInit(): void {
    this.loadWarehouses();
    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadWarehouses(): void {
    this.isLoading = true;
    this.warehouseService.findToComboBox(this?.data?.plantId).subscribe({
      next: (res) => {
        this.warehouseList = res.data || [];
        this.filteredWarehouses = [...this.warehouseList];
        this.isLoading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error?.message || 'Error al cargar almacenes',
          'Cerrar',
          { duration: 5000 }
        );
        this.isLoading = false;
      },
    });
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm) => {
        this.filterWarehouses(searchTerm || '');
      });
  }

  private filterWarehouses(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredWarehouses = [...this.warehouseList];
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    this.filteredWarehouses = this.warehouseList.filter((warehouse) => {
      const nameMatch = warehouse.name.toLowerCase().includes(searchLower);
      const codeMatch = warehouse.externalKeys?.code?.toLowerCase().includes(searchLower);
      return nameMatch || codeMatch;
    });
  }

  // Checkbox logic
  toggleWarehouse(warehouseId: string): void {
    if (!this.checkIfHasPermission(this.PERMISSIONS_MATERIAL_WAREHOUSE.UPDATE, "No tienes permisos para actualizar los almacenes")) {
      return;
    }

    if (this.selectedWarehouseIds.has(warehouseId)) {
      this.selectedWarehouseIds.delete(warehouseId);
    } else {
      this.selectedWarehouseIds.add(warehouseId);
    }
  }

  isWarehouseSelected(warehouseId: string): boolean {
    return this.selectedWarehouseIds.has(warehouseId);
  }

  toggleAllFiltered(): void {
    if (this.isAllFilteredSelected()) {
      // Deseleccionar todos los filtrados
      this.filteredWarehouses.forEach((warehouse) => {
        this.selectedWarehouseIds.delete(warehouse._id.toString());
      });
    } else {
      // Seleccionar todos los filtrados
      this.filteredWarehouses.forEach((warehouse) => {
        this.selectedWarehouseIds.add(warehouse._id.toString());
      });
    }
  }

  isAllFilteredSelected(): boolean {
    if (this.filteredWarehouses.length === 0) return false;
    return this.filteredWarehouses.every((warehouse) =>
      this.selectedWarehouseIds.has(warehouse._id.toString())
    );
  }

  isSomeFilteredSelected(): boolean {
    if (this.filteredWarehouses.length === 0) return false;
    const selectedCount = this.filteredWarehouses.filter((warehouse) =>
      this.selectedWarehouseIds.has(warehouse._id.toString())
    ).length;
    return selectedCount > 0 && selectedCount < this.filteredWarehouses.length;
  }

  // Counters
  getSelectedCount(): number {
    return this.selectedWarehouseIds.size;
  }

  getFilteredCount(): number {
    return this.filteredWarehouses.length;
  }

  getSelectedFilteredCount(): number {
    return this.filteredWarehouses.filter((warehouse) =>
      this.selectedWarehouseIds.has(warehouse._id.toString())
    ).length;
  }

  // Toggle "Available in all"
  onAvailableInAllChange(value: boolean): void {
    this.availableInAll = value;
    if (value) {
      // Si se activa "todos los almacenes", limpiar selección individual
      this.selectedWarehouseIds.clear();
    }
  }

  // Change detection
  hasChanges(): boolean {
    // Verificar si cambió el toggle
    if (this.availableInAll !== this.originalAvailableInAll) {
      return true;
    }

    // Verificar si cambió la selección de almacenes
    if (this.selectedWarehouseIds.size !== this.originalWarehouseIds.size) {
      return true;
    }

    // Verificar si los IDs seleccionados son diferentes
    for (const id of this.selectedWarehouseIds) {
      if (!this.originalWarehouseIds.has(id)) {
        return true;
      }
    }

    return false;
  }

  // Save
  onSave(): void {
    if (!this.checkIfHasPermission(this.PERMISSIONS_MATERIAL_WAREHOUSE.UPDATE, "No tienes permisos para actualizar los almacenes")) {
      return;
    }

    if (!this.hasChanges()) {
      this.snackBar.open('No hay cambios para guardar', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    // Validar que si no está "todos los almacenes", al menos uno esté seleccionado
    if (!this.availableInAll && this.selectedWarehouseIds.size === 0) {
      this.snackBar.open(
        'Debe seleccionar al menos un almacén o activar "Disponible en todos los almacenes"',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSaving = true;

    const updateData = {
      warehouseIds: Array.from(this.selectedWarehouseIds),
      availableInAllWarehouses: this.availableInAll,
    };

    this.materialService.update(this.data.materialId, updateData).subscribe({
      next: () => {
        this.snackBar.open('Almacenes actualizados correctamente', 'Cerrar', {
          duration: 3000,
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.snackBar.open(
          err.error?.message || 'Error al actualizar almacenes',
          'Cerrar',
          { duration: 5000 }
        );
        this.isSaving = false;
      },
    });
  }

  onCancel(): void {
    if (this.hasChanges()) {
      const confirmation = this.fuseConfirmationService.open({
        title: 'Cambios sin guardar',
        message: '¿Está seguro de que desea cerrar sin guardar los cambios?',
        actions: {
          confirm: {
            label: 'Cerrar sin guardar',
            color: 'warn'
          },
          cancel: {
            label: 'Continuar editando'
          }
        }
      });

      confirmation.afterClosed().subscribe(result => {
        if (result === 'confirmed') {
          this.dialogRef.close(false);
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  // Helpers
  clearSearch(): void {
    this.searchControl.setValue('');
  }

  checkIfHasPermission(permission: string, errorMessage?: string): boolean {
    const hasPermission = this.permissionService.has(permission);

    if (!hasPermission && errorMessage) {
      this.handlerMessage.error({ message: errorMessage });
    }

    return hasPermission;
  }
}
